import React, { useEffect, useState } from "react";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Avatar, Button, Descriptions, Spin, Tag } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../app/store";
import { deleteSurveyById, fetchAllSurveys } from "../../app/surveySlice";
import { useDebounce } from "use-debounce";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb";
import CommonModal from "../../components/CommonModal";
import { toast } from "react-toastify";
import { hasPermission } from "../../utils/permission";
import { useNavigate } from "react-router-dom";
import { deleteFormById, fetchAllForms } from "../../app/formslice";
import Swal from "sweetalert2";
import SortIcons from "../../components/SortIcons/SortIcons";

const SurveyList = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const {
    formList,
    total: formTotalFromStore,
    loading: formLoading,
  } = useSelector((state: RootState) => state.form);
  const roleName = localStorage.getItem("role_name");
  const is_admin = roleName === "admin" ? 1 : 0;
  const { surveyList, total, loading } = useSelector(
    (state: RootState) => state.survey
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);
  const [page, setPage] = useState(1);
  const [pagesize] = useState(10);
  const [selectedSurvey, setSelectedSurvey] = useState<any>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [selectedSurveyId, setSelectedSurveyId] = useState<number | null>(null);
  const [formAvailable, setFormAvailable] = useState<number | null | undefined>(null);
  const [formPage, setFormPage] = useState(1);
  const [formPagesize] = useState(10);
  const [formSearch, setFormSearch] = useState("");
  const [debouncedFormSearch] = useDebounce(formSearch, 500);
  const [orderBy, setOrderBy] = useState<
    "id" | "project_name" | "name" | "start" | "end" | "status"
  >("id");
  const [orderDir, setOrderDir] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    const loadSurveys = async () => {
      try {
        await dispatch(
          fetchAllSurveys({
            page,
            pagesize,
            search: debouncedSearch,
            order_by: orderBy,
            order_dir: orderDir,
            is_admin,
          })
        ).unwrap();
        //  toast.success(res.message);
      } catch (err: any) {
        toast.error(err || "Failed to fetch surveys");
      }
    };
    loadSurveys();
  }, [dispatch, page, pagesize, debouncedSearch, orderBy, orderDir]);
  useEffect(() => {
    if (formModalVisible && selectedSurveyId) {
      dispatch(
        fetchAllForms({
          page: formPage,
          pagesize: formPagesize,
          survey_id: selectedSurveyId,
          search: debouncedFormSearch.trim(),
        })
      );
    }
  }, [
    formModalVisible,
    selectedSurveyId,
    formPage,
    formPagesize,
    debouncedFormSearch,
  ]);

  const handleOpenFormModal = (
    surveyId: number,
    form_available: number | null | undefined
  ) => {
    setFormAvailable(form_available);
    setSelectedSurveyId(surveyId);
    setFormPage(1);
    setFormSearch("");
    setFormModalVisible(true);
  };
  const handleDelete = async (surveyId: number): Promise<string> => {
    try {
      const result = await dispatch(deleteSurveyById(surveyId)).unwrap(); // example thunk
      setPage(1);
      return result.message || "Survey deleted successfully";
    } catch (error: any) {
      throw new Error(error?.message || "Failed to delete survey");
    }
  };

  const handleDeleteForm = async (
    survey_version_id: number,
    survey_id: number
  ): Promise<string> => {
    try {
      const result = await dispatch(
        deleteFormById({ survey_version_id, survey_id })
      ).unwrap();
      dispatch(
        fetchAllForms({
          page: 1,
          pagesize: formPagesize,
          survey_id: selectedSurveyId ? Number(selectedSurveyId) : undefined,
          search: debouncedFormSearch.trim(),
        })
      );
      setFormPage(1);
      return result.message || "Form deleted successfully"; // ✅ return this
    } catch (error: any) {
      throw new Error(error?.message || "Failed to delete form");
    }
  };

  const handleSort = (field: typeof orderBy) => {
    if (orderBy === field) {
      setOrderDir((prev) => (prev === "desc" ? "asc" : "desc"));
    } else {
      setOrderBy(field);
      setOrderDir("desc");
    }
    setPage(1);
  };
  return (
    <>
      <Breadcrumb pageName="Manage Survey" />
      <div className="rounded-sm border border-stroke bg-white px-5 pt-6 pb-2.5 shadow-md dark:border-strokedark dark:bg-boxdark sm:px-7.5 xl:pb-1">
        <div className="max-w-full overflow-x-auto">
          <div className="flex justify-end mb-4">
            <input
              type="text"
              placeholder="Search by project, survey name"
              value={search}
              onChange={(e) => {
                const value = e.target.value.replace(/^\s+/, "");
                setSearch(value);
                setPage(1);
              }}
              className="w-full sm:w-1/3 rounded-md border border-gray-300 px-4 py-2 text-sm shadow-sm transition-all focus:border-blue-500 focus:ring focus:ring-blue-200 focus:outline-none"
            />
          </div>
          <table className="w-full table-auto sm:table-fixed">
            <thead>
              <tr className="bg-gray-2 text-left dark:bg-meta-4">
                <th className="py-4 px-4 text-left">Sr No</th>
                <th
                  className="py-4 px-4 text-left cursor-pointer"
                  onClick={() => handleSort("project_name")}
                >
                  <div className="flex items-center gap-2">
                    <span>Project Name</span>
                    <SortIcons
                      isActive={orderBy === "project_name"}
                      direction={orderDir}
                    />
                  </div>
                </th>
                <th
                  className="py-4 px-4 text-left cursor-pointer"
                  onClick={() => handleSort("name")}
                >
                  <div className="flex items-center gap-2">
                    <span>Name</span>
                    <SortIcons
                      isActive={orderBy === "name"}
                      direction={orderDir}
                    />
                  </div>
                </th>
                <th
                  className="py-4 px-4 text-left cursor-pointer"
                  onClick={() => handleSort("start")}
                >
                  <div className="flex items-center gap-2">
                    <span>Start</span>
                    <SortIcons
                      isActive={orderBy === "start"}
                      direction={orderDir}
                    />
                  </div>
                </th>
                <th
                  className="py-4 px-4 text-left cursor-pointer"
                  onClick={() => handleSort("end")}
                >
                  <div className="flex items-center gap-2">
                    <span>End</span>
                    <SortIcons
                      isActive={orderBy === "end"}
                      direction={orderDir}
                    />
                  </div>
                </th>
                <th
                  className="py-4 px-4 text-left cursor-pointer"
                  onClick={() => handleSort("status")}
                >
                  <div className="flex items-center gap-2">
                    <span>Status</span>
                    <SortIcons
                      isActive={orderBy === "status"}
                      direction={orderDir}
                    />
                  </div>
                </th>
                <th className="py-4 px-4 text-left">Form Info</th>
                <th className="py-4 px-4 text-left">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-6">
                    <Spin />
                  </td>
                </tr>
              ) : surveyList.length > 0 ? (
                surveyList.map((survey, index) => (
                  <tr key={survey.id}>
                    <td className="border-b py-4 px-4">
                      {(page - 1) * pagesize + index + 1}
                    </td>
                    <td className="border-b py-4 px-4">
                      {survey.project?.name || "--"}
                    </td>
                    <td className="border-b py-4 px-4">
                      {survey.name || "--"}
                    </td>
                    <td className="border-b py-4 px-4">
                      {survey.start
                        ? new Date(survey.start).toLocaleDateString("en-GB") // ✅ 'en-GB' gives dd/mm/yyyy
                        : "--"}
                    </td>
                    <td className="border-b py-4 px-4">
                      {survey.end
                        ? new Date(survey.end).toLocaleDateString("en-GB") // ✅ 'en-GB' gives dd/mm/yyyy
                        : "--"}
                    </td>
                    <td className="border-b py-4 px-4">
                      <Tag
                        color={
                          survey.status?.toLowerCase() === "active"
                            ? "green"
                            : survey.status?.toLowerCase() === "inactive"
                            ? "red"
                            : "default"
                        }
                        className="uppercase font-medium"
                      >
                        {survey.status ? survey.status.toUpperCase() : "--"}
                      </Tag>
                    </td>

                    <td className="border-b py-4 px-4">
                      {(survey.form_available ?? 0) > 0 ? (
                        <Button
                          type="default"
                          icon={<FileTextOutlined />}
                          onClick={() =>
                            handleOpenFormModal(
                              survey.id,
                              survey.form_available
                            )
                          }
                          className="rounded-md border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white transition-all duration-200"
                        >
                          View Forms
                        </Button>
                      ) : (
                        <Button
                          type="default"
                          icon={<PlusOutlined />}
                          onClick={() =>
                            navigate("/dashboard/formCreation", {
                              state: {
                                survey_id: survey.id,
                                survey_version_id: 0,
                                form_available: survey.form_available,
                              },
                            })
                          }
                          className="rounded-md border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white transition-all duration-200"
                        >
                          Create Form
                        </Button>
                      )}
                    </td>
                    <td className="border-b py-4 px-4">
                      <div className="flex gap-2">
                        {hasPermission("survey-view") && (
                          <button
                            onClick={() => {
                              setSelectedSurvey(survey);
                              setIsModalVisible(true);
                            }}
                          >
                            <EyeOutlined />
                          </button>
                        )}
                        {hasPermission("survey-update") && (
                          <button
                            onClick={() =>
                              navigate("/dashboard/updatesurvey", {
                                state: {
                                  id: survey.id,
                                },
                                //state: { survey },
                              })
                            }
                          >
                            <EditOutlined className="hover:text-green-600" />
                          </button>
                        )}
                        {hasPermission("survey-delete") && (
                          <button
                            className="hover:text-red-500"
                            onClick={async () => {
                              const result = await Swal.fire({
                                title:
                                  "Are you sure you want to delete this survey?",
                                text: "You won't be able to revert this!",
                                icon: "warning",
                                showCancelButton: true,
                                confirmButtonColor: "#d33",
                                cancelButtonColor: "#3085d6",
                                confirmButtonText: "Yes, delete it!",
                              });

                              if (result.isConfirmed) {
                                try {
                                  const message = await handleDelete(survey.id); // assume this returns a string
                                  await Swal.fire({
                                    title: "Deleted!",
                                    text: message,
                                    icon: "success",
                                    timer: 1500,
                                    showConfirmButton: false,
                                  });
                                } catch (err: any) {
                                  await Swal.fire({
                                    title: "Error",
                                    text:
                                      err?.message || "Failed to delete survey",
                                    icon: "error",
                                  });
                                }
                              }
                            }}
                          >
                            <DeleteOutlined />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="text-center font-medium py-4">
                    No result found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {surveyList && surveyList.length > 0 && (
            <div className="flex justify-between items-center mt-4 mb-2 flex-wrap gap-2">
              <div className="text-sm text-gray-600">
                Showing {(page - 1) * pagesize + 1} to{" "}
                {Math.min(page * pagesize, total)} of {total} surveys
              </div>

              <div className="flex items-center gap-1 flex-wrap">
                {/* Previous Button */}
                <button
                  className={`px-3 py-1.5 text-sm rounded-md border ${
                    page === 1
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "hover:bg-blue-100 text-blue-600 border-blue-300"
                  }`}
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                >
                  &lt;
                </button>

                {/* Page Numbers with Ellipsis */}
                {[...Array(Math.ceil(total / pagesize)).keys()]
                  .map((n: number) => n + 1)
                  .filter((p: number) => {
                    return (
                      p === 1 ||
                      p === Math.ceil(total / pagesize) ||
                      (p >= page - 1 && p <= page + 1)
                    );
                  })
                  .reduce((acc: (number | string)[], val: number, i, arr) => {
                    if (i > 0 && val - (arr[i - 1] as number) > 1) {
                      acc.push("...");
                    }
                    acc.push(val);
                    return acc;
                  }, [])
                  .map((p: number | string, idx: number) =>
                    typeof p === "string" ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="px-3 py-1.5 text-sm text-gray-500"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`px-3 py-1.5 text-sm rounded-md border ${
                          p === page
                            ? "bg-gray-300 text-gray-800"
                            : "hover:bg-blue-100 text-blue-600 border-blue-300"
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}

                {/* Next Button */}
                <button
                  className={`px-3 py-1.5 text-sm rounded-md border ${
                    page * pagesize >= total
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "hover:bg-blue-100 text-blue-600 border-blue-300"
                  }`}
                  onClick={() => setPage((prev) => prev + 1)}
                  disabled={page * pagesize >= total}
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
        <CommonModal
          visible={isModalVisible}
          onClose={() => {
            setIsModalVisible(false);
            setSelectedSurvey(null);
          }}
          title={
            <div className="flex items-center gap-2">
              <Avatar icon={<EyeOutlined />} />
              <span>Survey Details</span>
            </div>
          }
        >
          {!selectedSurvey ? (
            <div className="text-center py-6">
              <Spin />
            </div>
          ) : (
            <Descriptions
              column={1}
              bordered
              size="middle"
              labelStyle={{
                fontWeight: 600,
                backgroundColor: "#f9f9f9",
                width: "160px",
              }}
              contentStyle={{ backgroundColor: "#fff", color: "#262626" }}
            >
              <Descriptions.Item label="Name">
                {selectedSurvey.name || "--"}
              </Descriptions.Item>
              <Descriptions.Item label="Project Name">
                {selectedSurvey.project?.name || "--"}
              </Descriptions.Item>
              <Descriptions.Item label="Start">
                {selectedSurvey.start
                  ? new Date(selectedSurvey.start).toLocaleDateString("en-GB") // ✅ 'en-GB' gives dd/mm/yyyy
                  : "--"}
              </Descriptions.Item>
              <Descriptions.Item label="End">
                {selectedSurvey.end
                  ? new Date(selectedSurvey.end).toLocaleDateString("en-GB") // ✅ 'en-GB' gives dd/mm/yyyy
                  : "--"}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag
                  color={
                    selectedSurvey.status?.toLowerCase() === "active"
                      ? "green"
                      : selectedSurvey.status?.toLowerCase() === "inactive"
                      ? "red"
                      : "default"
                  }
                  className="uppercase font-medium"
                >
                  {selectedSurvey.status?.toUpperCase() || "--"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {selectedSurvey.created_at
                  ? new Date(selectedSurvey.created_at).toLocaleString(
                      "en-IN",
                      {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      }
                    )
                  : "--"}
              </Descriptions.Item>
            </Descriptions>
          )}
        </CommonModal>
        <CommonModal
          title="Form Info"
          visible={formModalVisible}
          onClose={() => setFormModalVisible(false)}
          width={900}
        >
          <div className="mb-4 flex justify-end">
            <input
              type="text"
              placeholder="Search by name"
              value={formSearch}
              onChange={(e) => {
                const value = e.target.value.replace(/^\s+/, "");
                setFormSearch(value);
                setFormPage(1);
              }}
              className="w-1/2 rounded border px-3 py-1 text-sm shadow-sm focus:border-blue-500 focus:outline-none"
            />
          </div>
          <table className="w-full table-fixed">
            <thead>
              <tr className="bg-gray-2 text-left dark:bg-meta-4">
                <th className="py-4 px-4 text-left">Sr No</th>
                <th className="py-4 px-4 text-left">Survey Name</th>
                <th className="py-4 px-4 text-left">Created At</th>
                <th className="py-4 px-4 text-left">Updated At</th>
                <th className="py-4 px-4 text-left">Action</th>
              </tr>
            </thead>
            <tbody>
              {formLoading ? (
                <tr>
                  <td colSpan={5} className="text-center py-6">
                    <Spin />
                  </td>
                </tr>
              ) : formList.length > 0 ? (
                formList.map((form, index) => (
                  <tr key={form.id}>
                    <td className="border-b py-4 px-4">
                      {(formPage - 1) * formPagesize + index + 1}
                    </td>
                    <td className="border-b py-4 px-4">{form.survey_name}</td>
                    <td className="border-b py-4 px-4">
                      {form.created_at
                        ? new Date(form.created_at).toLocaleString("en-GB", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })
                        : "--"}
                    </td>
                    <td className="border-b py-4 px-4">
                      {form.updated_at
                        ? new Date(form.updated_at).toLocaleString("en-GB", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })
                        : "--"}
                    </td>
                    <td className="border-b py-4 px-4">
                      <div className="flex gap-2">
                        {hasPermission("survey-update") && (
                          <button
                            onClick={() =>
                              navigate("/dashboard/formUpdation", {
                                state: {
                                  survey_version_id: form.survey_version_id,
                                  survey_id: form.survey_id,
                                  form_available: formAvailable,
                                },
                              })
                            }
                            className="hover:text-green-600"
                            title="Edit"
                          >
                            <EditOutlined />
                          </button>
                        )}

                        {hasPermission("survey-delete") && (
                          <button
                            className="hover:text-red-500 text-base"
                            onClick={async () => {
                              const result = await Swal.fire({
                                title:
                                  "Are you sure you want to delete this Form?",
                                text: "You won't be able to revert this!",
                                icon: "warning",
                                showCancelButton: true,
                                confirmButtonColor: "#d33",
                                cancelButtonColor: "#3085d6",
                                confirmButtonText: "Yes, delete it!",
                              });

                              if (result.isConfirmed) {
                                try {
                                  const deleteResult = await handleDeleteForm(
                                    form.survey_version_id,
                                    form.survey_id
                                  );
                                  Swal.fire({
                                    title: "Deleted!",
                                    text: deleteResult,
                                    icon: "success",
                                    timer: 1500,
                                    showConfirmButton: false,
                                  });
                                  dispatch(
                                    fetchAllSurveys({
                                      page: 1,
                                      pagesize,
                                      search: debouncedSearch,
                                      order_by: orderBy,
                                      order_dir: orderDir,
                                      is_admin,
                                    })
                                  );
                                } catch (err: any) {
                                  Swal.fire({
                                    title: "Error",
                                    text: err?.message || "Deletion failed",
                                    icon: "error",
                                  });
                                }
                              }
                            }}
                          >
                            <DeleteOutlined />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="text-center font-medium py-4">
                    No result found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {formList && formList.length > 0 && (
            <div className="flex justify-between items-center mt-4 mb-2 flex-wrap gap-2">
              <div className="text-sm text-gray-600">
                Showing {(formPage - 1) * formPagesize + 1} to{" "}
                {Math.min(formPage * formPagesize, formTotalFromStore)} of{" "}
                {formTotalFromStore} forms
              </div>

              <div className="flex items-center gap-1 flex-wrap">
                {/* Previous Button */}
                <button
                  className={`px-3 py-1.5 text-sm rounded-md border ${
                    formPage === 1
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "hover:bg-blue-100 text-blue-600 border-blue-300"
                  }`}
                  onClick={() => setFormPage((prev) => Math.max(prev - 1, 1))}
                  disabled={formPage === 1}
                >
                  &lt;
                </button>

                {/* Page Numbers with Ellipsis */}
                {[...Array(Math.ceil(formTotalFromStore / formPagesize)).keys()]
                  .map((n: number) => n + 1)
                  .filter((p: number) => {
                    return (
                      p === 1 ||
                      p === Math.ceil(formTotalFromStore / formPagesize) ||
                      (p >= formPage - 1 && p <= formPage + 1)
                    );
                  })
                  .reduce((acc: (number | string)[], val: number, i, arr) => {
                    if (i > 0 && val - (arr[i - 1] as number) > 1) {
                      acc.push("...");
                    }
                    acc.push(val);
                    return acc;
                  }, [])
                  .map((p: number | string, idx: number) =>
                    typeof p === "string" ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="px-3 py-1.5 text-sm text-gray-500"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setFormPage(p)}
                        className={`px-3 py-1.5 text-sm rounded-md border ${
                          p === formPage
                            ? "bg-gray-300 text-gray-800"
                            : "hover:bg-blue-100 text-blue-600 border-blue-300"
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}

                {/* Next Button */}
                <button
                  className={`px-3 py-1.5 text-sm rounded-md border ${
                    formPage * formPagesize >= formTotalFromStore
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "hover:bg-blue-100 text-blue-600 border-blue-300"
                  }`}
                  onClick={() => setFormPage((prev) => prev + 1)}
                  disabled={formPage * formPagesize >= formTotalFromStore}
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </CommonModal>
      </div>
    </>
  );
};

export default SurveyList;
